import { useTenant } from '@/hooks';
import {
  useGetInternalProjectsDetailedAppliedQuery,
  useGetInternalProjectsQuery,
} from '@/state';
import type { ApiErrorResponse } from '@/types';
import { ProjectsListPanel } from '../projects-list-panel';
import { useMemo, useState } from 'react';
import { CreationWizardManager } from '../creation-wizard/manager';
import { useClientPermissions } from '@/hooks/use-client-permissions';

type InternalProjectsProps = {
  assignedInternalProjects: boolean;
  isDetailedApplied: boolean;
  tabName: string;
};

export const InternalProjects = ({
  assignedInternalProjects,
  isDetailedApplied,
  tabName,
}: InternalProjectsProps) => {
  const { tenant } = useTenant();
  const [showCreateProject, setShowCreateProject] = useState(false);
  const { isTeacher, isSchoolAdmin, isStudent, isLoading } = useClientPermissions();


  const title = (assignedInternalProjects || isDetailedApplied) ? 'Assigned Internal Projects' : 'Not Assigned Internal Projects';

  // Always fetch both for merging logic in Not Assigned tab
  const overviewQuery = useGetInternalProjectsQuery(
    { tenant: tenant! },
    { skip: !tenant || tabName === "assigned" }
  );



  const detailedQuery = useGetInternalProjectsDetailedAppliedQuery(
    {
      tenant: tenant!,
      isTeacher: isTeacher || isSchoolAdmin
    },
    { skip: !tenant || !(isTeacher || isSchoolAdmin || isStudent) }
  );

  const view = useMemo(() => {
    let projects: any[] = [];
    const isNotAssignedTab = !assignedInternalProjects && !isDetailedApplied;

    if (isNotAssignedTab) {
      // Start with overview projects
      const overview = overviewQuery.data || [];

      projects = overview.map(op => {
        return op;
      }).filter(p => {
        const isNotAssignedStatus = String(p.project_status || '').toUpperCase() === "NOT ASSIGNED";
        const isStatusOne = Number(p.status) === 1;
        return isNotAssignedStatus || isStatusOne;
      });
    } else {
      const q = isDetailedApplied ? detailedQuery : overviewQuery;
      projects = q.data || [];
    }

    const currentQuery = isDetailedApplied ? detailedQuery : overviewQuery;

    return {
      projects,
      isLoading: overviewQuery.isLoading || detailedQuery.isLoading,
      isFetching: overviewQuery.isFetching || detailedQuery.isFetching,
      error: currentQuery.error as ApiErrorResponse | undefined,
      onRetry: currentQuery.refetch,
    };
  }, [isDetailedApplied, overviewQuery, detailedQuery]);

  return (
    <>
      <CreationWizardManager
        open={showCreateProject}
        onOpenChange={setShowCreateProject}
        projectType="INTERNAL"
      />

      <ProjectsListPanel
        title={title}
        projects={view.projects}
        isLoading={view.isLoading || isLoading}
        isFetching={view.isFetching}
        createLabel="Create Internal Project"
        projectType="INTERNAL"
        onCreate={isStudent ? undefined : () => setShowCreateProject(true)}
        onRetry={view.onRetry}
        error={view.error}
        assignedInternalProjects={assignedInternalProjects}
      // isDetailedApplied={isDetailedApplied} // only if your panel needs it
      />
    </>
  );
};
